import { isSameYear, isToday, isValid, parseISO } from 'date-fns';
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import i18n, { t } from 'i18next';

import { getDateFnsCurrentLocale } from '../i18n/I18n';

export function isValidDate(date) {
    return date instanceof Date && isValid(date);
}

export function formatDateTime(date) {
    if (!date) {
        return '';
    }
    const dateTimeObject = isValidDate(date) ? date : parseISO(date);
    return format(dateTimeObject, `PP '${t('at')}' p`, {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeShort(date) {
    if (!date) {
        return '';
    }
    const dateTimeObject = isValidDate(date) ? date : parseISO(date);
    return format(dateTimeObject, 'Pp', { locale: getDateFnsCurrentLocale(i18n.language) });
}

export function formatDate(date) {
    if (!date) {
        return '';
    }
    const dateTimeObject = isValidDate(date) ? date : parseISO(date);

    return format(dateTimeObject, 'PP', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatTime(date) {
    if (!date) {
        return '';
    }
    const dateTimeObject = isValidDate(date) ? date : parseISO(date);

    return format(dateTimeObject, 'p', {
        locale: getDateFnsCurrentLocale(i18n.language),
    });
}

export function formatDateTimeFromRange(isRangeDefined, date) {
    return isRangeDefined && date ? formatDateTime(date) : '';
}

export function formatDateTimeRange(start, end) {
    const startDate = isValidDate(start) ? new Date(start) : null;
    const endDate = isValidDate(end) ? new Date(end) : null;
    if (!start) return '-';

    if (!endDate) return formatDateTime(startDate);

    if (startDate.toDateString() === endDate.toDateString()) {
        // Show date once and time range
        const locale = getDateFnsCurrentLocale(i18n.language);
        return format(startDate, 'PPp', { locale }).replace(
            format(startDate, 'p', { locale }),
            `${format(startDate, 'p', { locale })} - ${format(endDate, 'p', { locale })}`
        );
    }

    return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
}

export function formatRelativeDate(date) {
    if (isToday(date)) {
        return format(date, "HH'h'mm");
    }
    if (isSameYear(date, new Date())) {
        return format(date, 'dd MMM');
    }
    return formatDate(date, 'dd/MM/yy');
}

export function formatDateTimeRangeForOrder(datetimeRange, formatter = formatDateTime) {
    if (!datetimeRange) {
        return t('Not specified');
    }
    const from = datetimeRange?.start?.value || datetimeRange?.start;
    const to = datetimeRange?.end?.value || datetimeRange?.end;
    const startDatetime = from ? new Date(datetimeRange?.start?.value || datetimeRange?.start) : null;
    const endDatetime = to ? new Date(datetimeRange?.end?.value || datetimeRange?.end) : null;

    if (startDatetime && format(startDatetime, 'HH:mm') === '08:00' && format(endDatetime, 'HH:mm') === '12:00') {
        return `${formatDate(startDatetime)} - ${t('Morning')}`;
    }
    if (endDatetime && format(startDatetime, 'HH:mm') === '14:00' && format(endDatetime, 'HH:mm') === '18:00') {
        return `${formatDate(startDatetime)} - ${t('Afternoon')}`;
    }
    return formatter(startDatetime);
}

export function formatDateToLocaleForExport(date) {
    if (!date) {
        return '';
    }
    const dateTimeObject = isValidDate(date) ? date : parseISO(date);

    return format(dateTimeObject, 'yyyy-MM-dd HH:mm:ss');
}

export const formatOpeningHours = (openingHours, userTimeZone) => {
    return openingHours.map(({ hour, minute, timezone }) => {
        const now = new Date();
        const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
        const utcDate = zonedTimeToUtc(localDate, timezone);
        const userDate = utcToZonedTime(utcDate, userTimeZone);
        return format(userDate, 'HH:mm', { timeZone: userTimeZone });
    });
};
